import { Injectable, WritableSignal, Signal, computed, effect, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../services/api-services/api.service';
import {
  FilterCriteria,
  Place,
  RecurrencePattern,
  SearchRequestNew,
} from '../interfaces/interfaces';
import { catchError, finalize, of, tap, firstValueFrom } from 'rxjs';
import { FloorplanStore } from './floorplan.store';
import { AvatarService } from '../services/avatarService/avatar.service';
import { environment } from 'src/environments/environment';

type SearchRequestType = '' | 'series' | 'single';

const localStorageSettingsKey = 'placesSettings';

@Injectable({
  providedIn: 'root',
})
export class PlacesStore {
  // Signals
  private _allPlacesSignal: WritableSignal<Place[]> = signal<Place[]>([]);
  private _loadingSignal: WritableSignal<boolean> = signal<boolean>(false);
  private _errorSignal: WritableSignal<string | null> = signal<string | null>(
    null
  );
  private _searchRequestSignal: WritableSignal<SearchRequestNew | null> =
    signal<SearchRequestNew | null>(null);

  private _filterCriteriaSignal: WritableSignal<FilterCriteria> =
    signal<FilterCriteria>(
      localStorage.getItem(localStorageSettingsKey)
        ? JSON.parse(localStorage.getItem(localStorageSettingsKey))
        : {
            country: '',
            city: '',
            street: '',
            floor: '',
            floorNumber: '',
            tag: '',
          }
    );

  private _lastFetchTime: WritableSignal<number> = signal<number>(0);

  // Computed signals
  public places: Signal<Place[]> = this._allPlacesSignal.asReadonly();

  public searchRequest: Signal<SearchRequestNew | null> =
    this._searchRequestSignal.asReadonly();
  public searchRequestType: Signal<SearchRequestType> = computed(() => {
    if (this.searchRequest() == null) return '';
    if (this.searchRequest()?.recurrencePattern != null) return 'series';
    return 'single';
  });

  public filterCriteria: Signal<FilterCriteria> =
    this._filterCriteriaSignal.asReadonly();
  public isFilterCriteriaActive: Signal<boolean> = computed(() => {
    return Object.values(this._filterCriteriaSignal()).some(
      (value) => value !== ''
    );
  });
  public uniquePlacesTagsAndAddresses: Signal<any> = computed(() => {
    return this.generateUniquePlacesTagsAndAddresses(this._allPlacesSignal());
  });

  public favoritePlaces: Signal<Place[]> = computed(() =>
    this._allPlacesSignal()
      .filter((place) => {
        return place.isFav;
      })
      .sort((a, b) => a.favIndexOrdering - b.favIndexOrdering)
  );

  public notFavoritePlaces: Signal<Place[]> = computed(() =>
    this._allPlacesSignal().filter((place) => {
      return !place.isFav;
    })
  );

  public loading: Signal<boolean> = this._loadingSignal.asReadonly();
  public error: Signal<string | null> = this._errorSignal.asReadonly();

  private apiService: ApiService = inject(ApiService);
  private floorplanStore: FloorplanStore = inject(FloorplanStore);
  private avatarService: AvatarService = inject(AvatarService);
  constructor() {}

  // Actions
  public setSearchRequest(searchRequest: SearchRequestNew | null) {
    this._searchRequestSignal.set(searchRequest);
    this.fetchAvailablePlaces(true);
  }

  public setFilterCriteria(filterCriteria: FilterCriteria) {
    this._filterCriteriaSignal.set(filterCriteria);
  }

  public clearFilterCriteria() {
    this._filterCriteriaSignal.set({
      country: '',
      city: '',
      street: '',
      floor: '',
      floorNumber: '',
      tag: '',
    });
  }

  public resetStoreData() {
    this._allPlacesSignal.set([]);
    this._searchRequestSignal.set(null);
    this._filterCriteriaSignal.set({
      country: '',
      city: '',
      street: '',
      floor: '',
      floorNumber: '',
      tag: '',
    });
    localStorage.removeItem(localStorageSettingsKey);
  }

  public setPlacePending(placeEmailAddress: string) {
    let place = this._allPlacesSignal().find(
      (place) => place.emailAddress == placeEmailAddress
    );
    place.pending = true;
    place.isAvailable = false;
  }

  public getPlaceByEmailAddress(emailAddress: string): Place | null {
    return this._allPlacesSignal().find(
      (place) => place.emailAddress == emailAddress
    );
  }

  public updatePlaceAvailabilityLocal(place: Place) {
    //for now only update if no searchRequest
    if (this.searchRequest() == null) {
      this._allPlacesSignal.update((places) => {
        return places.map((p) => {
          if (
            p.emailAddress.toLowerCase() == place.emailAddress.toLowerCase()
          ) {
            return {
              ...p,
              isAvailable: place.isAvailable,
              pending: false,
              availableSpace: place.availableSpace,
            };
          }
          return p;
        });
      });
    }
  }

  // API calls
  public fetchAvailablePlaces(forceRefresh: boolean = false) {
    const currentTime = Date.now();
    const timeSinceLastFetch = currentTime - this._lastFetchTime();

    // If not a force refresh and less than 1 minute has passed, skip fetching
    if (!forceRefresh && timeSinceLastFetch < 60000) {
      console.log(
        'Skipping fetch - last fetch was',
        Math.round(timeSinceLastFetch / 1000),
        'seconds ago'
      );
      return Promise.resolve();
    }

    this._loadingSignal.set(true);
    this._allPlacesSignal.set([]);
    this._errorSignal.set(null);

    return firstValueFrom(
      this.apiService.getPlacesAvailability(this.searchRequest()).pipe(
        tap((places: Place[]) => {
          this.mapPlacesToFloorplans(places);
          this.mapPlacesToAvatars(places);

          this._allPlacesSignal.set(places);
          this._loadingSignal.set(false);

          // Update the last fetch time on successful fetch
          this._lastFetchTime.set(currentTime);
        }),
        catchError((error) => {
          this._errorSignal.set(error.message || 'Failed to fetch places');
          this._loadingSignal.set(false);
          return of([]);
        })
      )
    );
  }

  public fetchOnePlaceAvailability(
    start: Date,
    end: Date,
    recurrencePattern: RecurrencePattern,
    oldStartTime: Date,
    oldEndTime: Date,
    place: Place
  ): Promise<boolean> {
    return firstValueFrom(
      this.apiService.getOnePlaceAvailability(
        start,
        end,
        recurrencePattern,
        oldStartTime,
        oldEndTime,
        place
      )
    );
  }

  public toggleFavorite(place: Place) {
    place.isLoading = true;
    this.apiService
      .toggleFavoritePlace(place.emailAddress)
      .pipe(
        finalize(() => {
          this._allPlacesSignal.update((places) => {
            return places.map((p) => {
              if (p.emailAddress == place.emailAddress) {
                p.isLoading = false;
                if (p.isFav) {
                  return { ...p, isFav: false };
                } else {
                  return {
                    ...p,
                    isFav: true,
                    favIndexOrdering: this.favoritePlaces().length,
                  };
                }
              }
              return p;
            });
          });
        })
      )
      .subscribe();
  }

  // HELPERS

  private mapPlacesToAvatars(places: Place[]) {
    const avatars = this.avatarService.avatarList();
    places.forEach((place) => {
      const avatar = avatars.find((avatar) => avatar.upn == place.emailAddress);
      place.avatar = avatar?.avatar;
    });
  }

  private mapPlacesToFloorplans(places: Place[]) {
    try {
      const floorplans = this.floorplanStore.allFloorplansList();
      if (!floorplans || floorplans.length === 0) {
        console.warn('No floorplans available for mapping');
        return;
      }

      places.forEach((place) => {
        try {
          if (!place || !place.emailAddress) {
            return; // Skip this booking if place or emailAddress is missing
          }

          const matchingFloorplan = floorplans.find((floorplan) => {
            try {
              // Skip data URLs
              if (floorplan.floorMapImage.startsWith('data')) {
                return false;
              }

              // Parse JSON safely
              const parsedImage = JSON.parse(floorplan.floorMapImage);

              // Check if objects array exists and find matching object
              if (parsedImage.objects && Array.isArray(parsedImage.objects)) {
                return parsedImage.objects.some(
                  (object) => object.name === place.emailAddress
                );
              }

              return false;
            } catch (parseError) {
              console.warn('Failed to parse floorplan image JSON:', parseError);
              return false;
            }
          });

          place.floorMapId = matchingFloorplan?._id;
        } catch (bookingError) {
          console.warn(
            'Error processing place for floorplan mapping:',
            bookingError
          );
        }
      });
    } catch (error) {
      console.error('Error in mapPlacesToFloorplans:', error);
    }
  }

  public findMatchingPlaces(
    place: Place,
    query: string,
    filterCriteria: FilterCriteria = null
  ) {
    let isMatchingQuery = true;
    if (filterCriteria != null) {
      isMatchingQuery = Object.keys(filterCriteria).every((key) => {
        const criteriaValue = filterCriteria[key];
        // If no filter value is set, allow it
        if (!criteriaValue || criteriaValue === '') {
          return true;
        }

        if (key == 'tag') {
          return place.tag.toLowerCase() === criteriaValue.toLowerCase();
        }
        return place.address[key] === criteriaValue;
      });
    }
    if (query != '' && isMatchingQuery) {
      isMatchingQuery =
        place.displayName.toLowerCase().includes(query.toLowerCase()) ||
        place.emailAddress.toLowerCase().includes(query.toLowerCase()) ||
        Object.values(place.address)
          .join(' ')
          .toLowerCase()
          .includes(query.toLowerCase());
    }

    return isMatchingQuery;
  }

  private generateUniquePlacesTagsAndAddresses(places: Place[]): any {
    const locations = {};
    const countries = [];
    const cities = [];
    const streets = [];
    const floorLabels = [];
    const floorNumbers = [];
    const tags = [];
    places.forEach((place) => {
      if (place.tag && !tags?.includes(place.tag.toLowerCase())) {
        tags.push(place.tag.toLowerCase());
      }
      // Collect unique countries, cities, streets, floors, and floor numbers
      if (
        place.address?.country &&
        !countries?.includes(place.address.country)
      ) {
        countries.push(place.address.country);
      }

      if (place.address?.city && !cities?.includes(place.address.city)) {
        cities.push(place.address.city);
      }

      if (place.address?.street && !streets?.includes(place.address.street)) {
        streets.push(place.address.street);
      }

      if (place.address?.floor && !floorLabels?.includes(place.address.floor)) {
        floorLabels.push(place.address.floor);
      }

      if (
        place.address?.floorNumber &&
        !floorNumbers.includes(place.address.floorNumber)
      ) {
        floorNumbers.push(place.address.floorNumber);
      }

      // Destructuring the address for convenience
      const {
        country,
        city,
        street,
        floor: floorLabel,
        floorNumber,
      } = place.address || {};

      if (country) {
        // Ensure country exists in locations
        if (!locations[country]) {
          locations[country] = { value: country, cities: {} };
        }

        if (city) {
          // Ensure city exists under country
          if (!locations[country].cities[city]) {
            locations[country].cities[city] = { value: city, streets: {} };
          }

          if (street) {
            // Ensure street exists under city
            if (!locations[country].cities[city].streets[street]) {
              locations[country].cities[city].streets[street] = {
                value: street,
                floorLabels: {},
              };
            }

            if (floorLabel) {
              // Ensure floorLabel exists under street
              if (
                !locations[country].cities[city].streets[street].floorLabels[
                  floorLabel
                ]
              ) {
                locations[country].cities[city].streets[street].floorLabels[
                  floorLabel
                ] = { value: floorLabel, floorNumbers: [] };
              }

              if (floorNumber != null) {
                // Ensure floorNumbers array exists and push the floorNumber if not already present
                if (
                  !locations[country].cities[city].streets[street].floorLabels[
                    floorLabel
                  ].floorNumbers.includes(floorNumber)
                ) {
                  locations[country].cities[city].streets[street].floorLabels[
                    floorLabel
                  ].floorNumbers.push(floorNumber);
                }
              }
            }
          }
        }
      }
    });
    return {
      locations,
      countries,
      cities,
      streets,
      floorLabels,
      floorNumbers,
      tags,
    };
  }
}
